import { Component, Input, OnInit, inject } from '@angular/core';
import { TemplateSelectorData } from '@app/checkout/modules/checkout-shared/reducers/checkout.reducer';
import { Direction } from '@app/core/models/step';
import { ProductMediaItem } from '@app/product/reducers/product.reducer';
import { ProductEditorService } from '@app/product/services/product-editor.service';
import { NewRelic } from '@app/shared/services/newrelic.service';
import { ViewportService } from '@app/shared/services/viewport.service';
import { I18nService } from '@app/shared/utils/i18n.service';
import { isArrayEqual } from '@app/shared/utils/util';
import { firstValueFrom } from 'rxjs';

interface Template {
	templates: string[];
	thumbnail: string;
	size?: string;
	scope?: string;
}

export interface TemplateOptions {
	templatePrefix: string;
	templates: Template[];
}

export interface TemplateThumbnail {
	filename: string;
	key: string;
}

export interface Thumbnail {
	templates: string[];
	urls: api.ProductImageDto;
}

@Component({
	selector: 'g-template-selector',
	templateUrl: './template-selector.component.html',
	styleUrls: ['./template-selector.component.scss'],
	standalone: false,
})
export class TemplateSelectorComponent implements OnInit {
	private readonly productEditor = inject(ProductEditorService);
	private readonly newRelic = inject(NewRelic);
	private readonly i18n = inject(I18nService);
	readonly viewport = inject(ViewportService);

	@Input() readonly product: api.ProductDto;
	selectedOptions: api.OrderProductSelectedOptionsDto;
	selectedTemplates: string[];
	selectedThumbnailIndex = 0;
	productImages: string[] = [];
	templateOptions: TemplateOptions;
	templateThumbnails: TemplateThumbnail[];
	thumbnails: Thumbnail[] = [];
	imageUrls: any[] = [];
	private existingThumbnailFilenames: string[] = [];

	ngOnInit(): void {
		void this.setup();
	}

	async setup(): Promise<void> {
		return Promise.all([this.setProductInformation(), this.setSelectedTemplates()]).then<undefined>(
			() => undefined,
		);
	}

	collectStepData(direction?: Direction): TemplateSelectorData {
		if (direction === Direction.Previous) return;
		return { selectedTemplates: this.selectedTemplates };
	}

	onThumbnailClicked($event: { index: number; item: ProductMediaItem }): void {
		if (!$event || !Number.isFinite($event.index)) return;
		const template = this.thumbnails[$event.index];
		const templates = template?.templates ?? [];
		this.selectedTemplates = templates;
		this.productEditor.updateSelectedTemplates(templates);
		this.selectedThumbnailIndex = $event.index;
	}

	private async setProductInformation() {
		const selectedOptions = await firstValueFrom(this.productEditor.selectedOptions$);
		this.selectedOptions = selectedOptions || this.productEditor.initialOptions;
		this.templateOptions = this.i18n.getLocaleValue(
			this.product?.metadata?.product_editor_templates_json,
		);
		this.templateThumbnails = this.product?.properties?.templateThumbnails ?? [];
		this.existingThumbnailFilenames = this.templateThumbnails
			.map((thumbnail) => thumbnail?.filename)
			.filter((filename) => filename?.length > 0);
		this.filterThumbnails();
	}

	private async setSelectedTemplates() {
		// Templates might be already selected when editing cart item
		const selectedTemplates = await firstValueFrom(this.productEditor.selectedTemplates$);
		this.selectedTemplates = selectedTemplates;
		// Make the correct thumbnail active
		const selected = this.thumbnails?.find((t) => isArrayEqual(t.templates, this.selectedTemplates));
		if (selected) this.selectedThumbnailIndex = this.thumbnails?.indexOf(selected);
	}

	private filterThumbnails() {
		if (!this.hasValidTemplateOptions()) {
			const error = new Error('Invalid product_editor_templates_json');
			this.newRelic.noticeError(error, { productSku: this.product.sku });
			this.thumbnails = [];
			return;
		}
		const selectedScope = this.parseTemplateOption(this.selectedOptions?.scope);
		const selectedSize = this.parseTemplateOption(this.selectedOptions?.size);
		this.thumbnails = this.templateOptions.templates
			.filter((template) => {
				const size = this.parseTemplateOption(template.size);
				const scope = this.parseTemplateOption(template.scope);
				if (size && size !== selectedSize) return false;
				if (scope && scope !== selectedScope) return false;
				if (!this.isTemplateValid(template)) {
					return false;
				}
				return true;
			})
			.map((template) => {
				const templateThumbnail = this.templateThumbnails?.find((thumbnail) =>
					[thumbnail.filename, this.removeFileExtension(thumbnail.filename)].includes(
						template.thumbnail,
					),
				);
				const image = this.product?.images?.templateThumbnails?.find(
					(thumbnail) => thumbnail.filename === templateThumbnail?.filename,
				);
				return {
					templates: template.templates.map((t) => `${this.templateOptions.templatePrefix}${t}`),
					urls: image?.urls || {},
				};
			});
		this.imageUrls = this.thumbnails.map((template) => template.urls);
		this.selectedTemplates = this.thumbnails?.[0]?.templates ?? [];
		this.productEditor.updateSelectedTemplates(this.thumbnails?.[0]?.templates ?? []);
	}

	private hasValidTemplateOptions(): boolean {
		return this.templateOptions?.templates?.length > 0;
	}

	private isTemplateValid(template: Template): boolean {
		return (
			template?.thumbnail?.length > 0 &&
			this.existingThumbnailFilenames.some((filename) =>
				[filename, this.removeFileExtension(filename)].includes(template.thumbnail),
			)
		);
	}

	private parseTemplateOption(value: string | number): string | undefined {
		if (typeof value !== 'string' && !Number.isFinite(value)) return;
		return `${value}`.replaceAll(' ', '');
	}

	private removeFileExtension(filename: string): string {
		const parts = filename.split('.');
		return parts.slice(0, -1).join('.');
	}
}
